import { memo } from 'react'
import { CalendarDays, Check, CircleAlert, Film, GripVertical, Plus } from 'lucide-react'
import type { Program } from '../../types/index.js'
import { formatDateShort, formatDuration, formatTime } from '../../lib/utils.js'
import type { ProgramPartsBridge } from './ProgramForm.js'
import { MediaAssetManager } from './MediaAssetManager.js'

interface ProgramPartsTabProps {
  program: Program
  bridge: ProgramPartsBridge | null
}

/** Liste de montage issue de la maquette Pen `qEsqn`. L'éditeur de la partie
 * sélectionnée reste le ProgramForm monté à droite afin de ne perdre aucun
 * brouillon en changeant d'onglet. */
export const ProgramPartsTab = memo(function ProgramPartsTab({ program, bridge }: ProgramPartsTabProps) {
  const persistedParts = [program, ...(program.parts ?? [])]
  const navParts = bridge?.parts ?? persistedParts.map((part, index) => ({
    key: part.id,
    label: `Partie ${index + 1}`,
    durationSec: part.durationSec,
    isNew: false,
    overrideCount: part.partOverrideFields?.length ?? 0,
  }))
  const readyMedia = persistedParts.filter(part => (
    part.isLive || (part.mediaAssets ?? []).some(asset => !asset.isProxy && asset.status === 'OK')
  )).length
  const totalDurationSec = bridge?.totalDurationSec
    ?? persistedParts.reduce((total, part) => total + part.durationSec, 0)

  return (
    <section className="program-tab-parts" aria-label="Parties du programme">
      <div className="program-tab-summary">
        <SummaryValue value={String(navParts.length)} label="parties" tone="brand" />
        <SummaryValue value={formatDuration(totalDurationSec)} label="durée totale" />
        <SummaryValue value={formatDuration(bridge?.interPartBreakSec ?? program.interPartBreakSec ?? 0)} label="coupure entre parties" tone="success" />
        <SummaryValue value={`${readyMedia} / ${persistedParts.length}`} label="médias associés" tone="success" />
      </div>

      <div className="program-tab-parts__heading">
        <span>PARTIES · ORDRE DE DIFFUSION</span>
        <span>DURÉE {formatDuration(totalDurationSec)}</span>
      </div>

      <div className="program-tab-parts__list">
        {navParts.map((part, index) => {
          const persisted = persistedParts[index]
          const mainMedia = persisted?.mediaAssets?.find(asset => !asset.isProxy)
          const mediaReady = persisted?.isLive || mainMedia?.status === 'OK'
          const active = (bridge?.activePartIndex ?? 0) === index
          return (
            <button
              key={part.key}
              type="button"
              className={`program-part-row${active ? ' is-active' : ''}`}
              onClick={() => bridge?.onSelectPart(index)}
              aria-pressed={active}
            >
              <GripVertical size={14} aria-hidden="true" />
              <span className="program-part-row__index">{String(index + 1).padStart(2, '0')}</span>
              <span className="program-part-row__copy">
                <strong>{part.label}</strong>
                <small>
                  {mainMedia?.fileName ?? (part.isNew ? 'Nouvelle partie — enregistrez pour associer un média' : 'Aucun média associé')}
                  {mainMedia?.resolution ? ` · ${mainMedia.resolution}` : ''}
                </small>
              </span>
              <span className={`program-part-row__media${mediaReady ? ' is-ready' : ''}`}>
                {mediaReady ? 'MÉDIA OK' : part.isNew ? 'NOUVELLE' : 'SANS MÉDIA'}
              </span>
              <span className="program-part-row__duration">{formatDuration(part.durationSec)}</span>
            </button>
          )
        })}
      </div>

      <button
        type="button"
        className="tp-button tp-button--secondary program-tab-parts__add"
        onClick={bridge?.onAddPart}
        disabled={!bridge?.canAddPart}
      >
        <Plus size={14} aria-hidden="true" /> Ajouter une partie
      </button>
    </section>
  )
})

export const ProgramDiffusionsTab = memo(function ProgramDiffusionsTab({
  program, onAddToGrid,
}: {
  program: Program
  onAddToGrid?: (program: Program) => void
}) {
  const items = program.scheduleItems ?? []
  const stats = program.stats
  return (
    <section className="program-tab-surface" aria-label="Diffusions du programme">
      <header className="program-tab-surface__header">
        <div>
          <span className="program-tab-surface__eyebrow">PLANIFICATION</span>
          <h2>Diffusions</h2>
          <p>Passages programmés et synthèse de diffusion de cette fiche.</p>
        </div>
        <button type="button" className="tp-button tp-button--primary" onClick={() => onAddToGrid?.(program)} disabled={!onAddToGrid}>
          <Plus size={14} aria-hidden="true" /> Ajouter à la grille
        </button>
      </header>

      <div className="program-tab-summary program-tab-summary--content">
        <SummaryValue value={String(stats?.scheduledCount ?? items.length)} label="programmées" tone="brand" />
        <SummaryValue value={String(stats?.totalAirings ?? 0)} label="diffusions totales" />
        <SummaryValue value={String(stats?.rerunCount ?? program.legacyRerunCount ?? 0)} label="rediffusions" />
        <SummaryValue value={stats?.lastAiredAt ? formatDateShort(stats.lastAiredAt) : '—'} label="dernière diffusion" />
      </div>

      <div className="program-tab-card">
        <div className="program-tab-table program-tab-table--diffusions" role="table" aria-label="Diffusions programmées">
          <div className="program-tab-table__head" role="row">
            <span>Date</span><span>Heure</span><span>Grille</span><span>Durée</span><span>État</span>
          </div>
          {items.length === 0 ? (
            <EmptyState icon={<CalendarDays size={22} />} title="Aucune diffusion programmée" detail="Ajoutez cette fiche à une grille pour préparer son prochain passage." />
          ) : items.map(item => (
            <div key={item.id} className="program-tab-table__row" role="row">
              <span>{formatDateShort(item.startTime)}</span>
              <span className="is-data">{formatTime(item.startTime).slice(0, 5)}</span>
              <span>{item.grid?.name ?? 'Grille'}</span>
              <span className="is-data">{formatDuration(item.durationSec)}</span>
              <span><StatusBadge status={item.status} /></span>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
})

export const ProgramMediaTab = memo(function ProgramMediaTab({
  program, onChanged, disabled, disabledReason,
}: {
  program: Program
  onChanged?: () => void | Promise<void>
  disabled?: boolean
  disabledReason?: string
}) {
  const parts = [program, ...(program.parts ?? [])]
  const ready = parts.filter(part => part.isLive || (part.mediaAssets ?? []).some(asset => !asset.isProxy && asset.status === 'OK')).length
  return (
    <section className="program-tab-surface" aria-label="Médias du programme">
      <header className="program-tab-surface__header">
        <div>
          <span className="program-tab-surface__eyebrow">TECHNIQUE ANTENNE</span>
          <h2>Médias</h2>
          <p>Fichier principal, médias des parties et contrôles de disponibilité.</p>
        </div>
        <span className={`program-tab-readiness${ready === parts.length ? ' is-ready' : ''}`}>
          {ready === parts.length ? <Check size={14} /> : <CircleAlert size={14} />}
          {ready} / {parts.length} prêts
        </span>
      </header>
      <div className="program-tab-media-grid">
        {parts.map((part, index) => (
          <div key={part.id} className="program-tab-card program-tab-media-card">
            <div className="program-tab-media-card__title">
              <span className="program-part-row__index">{String(index + 1).padStart(2, '0')}</span>
              <div><strong>Partie {index + 1}</strong><small>{part.title || program.title}</small></div>
            </div>
            <MediaAssetManager
              program={part}
              title={part.isLive ? 'Source live' : 'Média associé'}
              onChanged={onChanged}
              disabled={disabled}
              disabledReason={disabledReason}
            />
          </div>
        ))}
      </div>
    </section>
  )
})

export const ProgramHistoryTab = memo(function ProgramHistoryTab({ program }: { program: Program }) {
  const history = program.asRunHistory ?? []
  const stats = program.stats
  return (
    <section className="program-tab-surface" aria-label="Historique du programme">
      <header className="program-tab-surface__header">
        <div>
          <span className="program-tab-surface__eyebrow">ASRUN</span>
          <h2>Historique</h2>
          <p>Passages réellement exécutés et écarts constatés à l’antenne.</p>
        </div>
      </header>

      <div className="program-tab-summary program-tab-summary--content">
        <SummaryValue value={String(stats?.completedAirings ?? history.length)} label="passages terminés" tone="brand" />
        <SummaryValue value={String(stats?.rerunCount ?? 0)} label="rediffusions" />
        <SummaryValue value={`${stats?.avgDriftSec && stats.avgDriftSec > 0 ? '+' : ''}${stats?.avgDriftSec ?? 0}s`} label="écart moyen" />
        <SummaryValue value={String(stats?.gapIncidents ?? 0)} label="incidents gap" tone={(stats?.gapIncidents ?? 0) > 0 ? 'warning' : 'success'} />
      </div>

      <div className="program-tab-card">
        <div className="program-tab-table program-tab-table--history" role="table" aria-label="Historique AsRun">
          <div className="program-tab-table__head" role="row">
            <span>Date prévue</span><span>Début réel</span><span>Durée réelle</span><span>Écart</span><span>Statut</span>
          </div>
          {history.length === 0 ? (
            <EmptyState icon={<Film size={22} />} title="Aucun passage enregistré" detail="L’historique apparaîtra après la première diffusion constatée." />
          ) : history.map(log => {
            const actualDuration = log.actualStart && log.actualEnd
              ? Math.round((new Date(log.actualEnd).getTime() - new Date(log.actualStart).getTime()) / 1000)
              : null
            const drift = log.durationDiffSec ?? 0
            return (
              <div key={log.id} className="program-tab-table__row" role="row">
                <span>{formatDateShort(log.scheduledStart)}</span>
                <span className="is-data">{log.actualStart ? formatTime(log.actualStart) : '—'}</span>
                <span className="is-data">{actualDuration == null ? '—' : formatDuration(actualDuration)}</span>
                <span className={`is-data${Math.abs(drift) > 30 ? ' is-warning' : ''}`}>{drift > 0 ? '+' : ''}{drift}s</span>
                <span><StatusBadge status={log.status} /></span>
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
})

const SummaryValue = memo(function SummaryValue({ value, label, tone = 'default' }: {
  value: string
  label: string
  tone?: 'default' | 'brand' | 'success' | 'warning'
}) {
  return <div className={`program-tab-summary__item is-${tone}`}><strong>{value}</strong><span>{label}</span></div>
})

const StatusBadge = memo(function StatusBadge({ status }: { status?: string | null }) {
  const value = status ?? 'PLANIFIÉ'
  const tone = value === 'OK' || value === 'COMPLETED' || value === 'VALIDATED' ? 'ok'
    : value === 'ERROR' || value === 'FAILED' ? 'danger' : 'neutral'
  return <span className={`program-tab-status is-${tone}`}>{value.replace(/_/g, ' ')}</span>
})

const EmptyState = memo(function EmptyState({ icon, title, detail }: { icon: React.ReactNode; title: string; detail: string }) {
  return <div className="program-tab-empty">{icon}<strong>{title}</strong><span>{detail}</span></div>
})
